import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import {
  PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from 'recharts';
import html2canvas from 'html2canvas';
import AppSidebar from '../../components/AppSidebar/AppSidebar';
import companiesApi from '../../api/companiesApi';
import companyBranchesApi from '../../api/companyBranchesApi';
import employeesApi from '../../api/employeesApi';
import companyAnalyticsApi from '../../api/companyAnalyticsApi';
import inbodyApi from '../../api/inbodyApi';
import labResultsApi from '../../api/labResultsApi';
import responsesApi from '../../api/responsesApi';
import styles from './CompanyDashboardPage.module.css';

function DownloadIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 3v12" />
      <path d="m7 10 5 5 5-5" />
      <path d="M5 21h14" />
    </svg>
  );
}

function fileNameFromTitle(title) {
  return title
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

function DownloadChartButton({ title }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async (event) => {
    const chartCard = event.currentTarget.closest(`.${styles.chartCard}`);
    if (!chartCard || downloading) return;

    setDownloading(true);
    try {
      const canvas = await html2canvas(chartCard, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
        ignoreElements: (element) => element.hasAttribute('data-download-control'),
      });
      const link = document.createElement('a');
      link.download = `${fileNameFromTitle(title) || 'grafica'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (error) {
      console.error('No se pudo descargar la gráfica:', error);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      className={styles.downloadChartButton}
      onClick={handleDownload}
      disabled={downloading}
      data-download-control
      aria-label={`Descargar ${title} en PNG`}
      title="Descargar gráfica en PNG"
    >
      <DownloadIcon />
      <span>{downloading ? 'Generando…' : 'PNG'}</span>
    </button>
  );
}

// ─── Colores ──────────────────────────────────────────────────────────────────
const AVATAR_COLORS = ['#00be9c','#1069f1','#6f42c1','#fd7e14','#20c997','#0dcaf0','#e83e8c','#6c757d'];
const METABOLIC_COLORS = ['#00be9c','#ffe951','#dc3545'];
const HRA_METABOLIC_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'La gráfica muestra la proporción de personas con riesgo metabólico bajo, medio o alto a partir de sus características de salud y factores asociados.',
  highlight: 'Datos considerados: edad, sexo biológico, IMC (Índice de Masa Corporal), enfermedades actuales, antecedentes familiares, tabaquismo y actividad física.',
  categories: [
    {
      name: 'Bajo',
      range: 'Sin condición activadora',
      explanation: 'No se cumple ninguna condición de riesgo alto ni se presenta IMC (Índice de Masa Corporal) mayor a 24, dislipidemias, tabaquismo, antecedentes familiares de riesgo medio o baja actividad física.',
    },
    {
      name: 'Medio',
      range: 'Al menos 1 condición',
      explanation: 'Sin llegar a riesgo alto, se activa por IMC (Índice de Masa Corporal) mayor a 24, dislipidemias, tabaquismo, antecedentes familiares de diabetes, hipertensión o infartos, o por baja actividad física.',
    },
    {
      name: 'Alto',
      range: 'Condición prioritaria',
      explanation: 'Se asigna por edad mayor a 60 años, más de una enfermedad propia de alto riesgo —diabetes, hipertensión arterial o insuficiencia renal—, o por acumular 3 o más factores de riesgo medio.',
    },
  ],
};
const IMC_COLORS       = ['#00be9c', '#ffe951', '#ffa040', '#fd7e14', '#dc3545'];
const IMC_CLASSIFICATION_CATEGORIES = [
  {
    name: 'Normal',
    range: 'Menor a 25',
    explanation: 'El IMC (Índice de Masa Corporal) está por debajo del primer umbral de riesgo.',
    detail: 'El peso está en proporción saludable con la estatura y se asocia con menor riesgo cardiovascular y metabólico. El IMC no distingue entre masa muscular y grasa, por lo que conviene interpretarlo junto con el porcentaje de grasa corporal. Suele relacionarse con alimentación balanceada, actividad física regular, sueño y manejo adecuado del estrés.',
  },
  {
    name: 'Sobrepeso',
    range: '25.0 a 29.9',
    explanation: 'El IMC (Índice de Masa Corporal) es igual o mayor a 25, pero menor a 30.',
    detail: 'Existe un exceso de peso que, sin ser obesidad, incrementa el riesgo de hipertensión, resistencia a la insulina y dislipidemias. Puede ser una etapa reversible con cambios de hábitos. Se asocia comúnmente con balance calórico positivo, sedentarismo y consumo elevado de azúcares y alimentos ultraprocesados.',
  },
  {
    name: 'Obesidad I',
    range: '30.0 a 35.9',
    explanation: 'El IMC (Índice de Masa Corporal) es igual o mayor a 30, pero menor a 36.',
    detail: 'La acumulación de grasa aumenta de forma moderada a alta el riesgo de diabetes tipo 2 y enfermedades cardiovasculares y articulares. Puede asociarse con sedentarismo sostenido, patrones alimentarios hipercalóricos, factores hormonales o genéticos y estrés crónico.',
  },
  {
    name: 'Obesidad II',
    range: '36.0 a 39.9',
    explanation: 'El IMC (Índice de Masa Corporal) es igual o mayor a 36, pero menor a 40.',
    detail: 'Representa un riesgo alto de comorbilidades metabólicas y cardiovasculares. Suele requerir acompañamiento multidisciplinario en nutrición, medicina y actividad física. Generalmente interviene una combinación prolongada de factores conductuales, metabólicos y ambientales.',
  },
  {
    name: 'Obesidad III',
    range: '40.0 o más',
    explanation: 'El IMC (Índice de Masa Corporal) es igual o mayor a 40.',
    detail: 'Representa un riesgo muy alto para la salud, con mayor probabilidad de complicaciones graves como apnea del sueño, enfermedad cardiovascular y limitación funcional. Requiere abordaje médico especializado y suele asociarse con factores múltiples y crónicos, incluidas condiciones metabólicas y hormonales.',
  },
];
const HRA_IMC_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'El IMC (Índice de Masa Corporal) se calcula dividiendo el peso en kilogramos entre la altura en metros al cuadrado. El resultado se redondea a un decimal antes de asignar la categoría.',
  highlight: 'IMC (Índice de Masa Corporal) = peso (kg) / [altura (m)]²',
  categories: IMC_CLASSIFICATION_CATEGORIES,
};
const INBODY_IMC_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'El IMC (Índice de Masa Corporal) representa la relación entre el peso y la altura. La gráfica muestra qué proporción de personas se encuentra en cada categoría.',
  highlight: 'IMC (Índice de Masa Corporal) = peso (kg) / [altura (m)]²',
  categories: IMC_CLASSIFICATION_CATEGORIES,
};
const INBODY_PBF_COLORS = ['#ffe951', '#00be9c', '#dc3545'];
const INBODY_PBF_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'El PBF (Porcentaje de Grasa Corporal) indica qué porcentaje del peso corporal corresponde a grasa. La gráfica muestra la proporción de personas por debajo, dentro o por encima de sus límites de referencia.',
  highlight: 'PBF (Porcentaje de Grasa Corporal) = masa de grasa corporal / peso corporal × 100',
  categories: [
    {
      name: 'Bajo',
      range: '< límite inferior',
      explanation: 'El porcentaje de grasa corporal está por debajo del límite inferior de referencia.',
      detail: 'Las reservas de grasa están por debajo de lo saludable y pueden afectar la función hormonal, inmunológica y la termorregulación. Puede asociarse con déficit calórico marcado, entrenamiento de alta intensidad, restricción alimentaria o alteraciones hormonales o clínicas.',
    },
    {
      name: 'Normal',
      range: 'Entre límites',
      explanation: 'El porcentaje está entre el límite inferior y el superior, incluyendo ambos valores.',
      detail: 'La proporción de grasa favorece un buen funcionamiento metabólico y hormonal. Suele relacionarse con equilibrio entre alimentación, actividad física y descanso.',
    },
    {
      name: 'Alto',
      range: '> límite superior',
      explanation: 'El porcentaje de grasa corporal supera el límite superior de referencia.',
      detail: 'Existe un exceso de tejido graso relacionado con mayor riesgo metabólico y cardiovascular, incluso cuando el peso o el IMC (Índice de Masa Corporal) parecen normales. Puede asociarse con sedentarismo, consumo elevado de azúcares y calorías, poca masa muscular, estrés y sueño insuficiente.',
    },
  ],
};
const INBODY_VFL_COLORS = ['#00be9c', '#ffe951', '#dc3545'];
const INBODY_VFL_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'El VFL (Nivel de Grasa Visceral) representa la grasa acumulada alrededor de los órganos internos. La gráfica muestra la proporción de personas con un nivel normal, alto o muy alto.',
  categories: [
    {
      name: 'Normal',
      range: 'Nivel 9 o menor',
      explanation: 'El nivel de grasa visceral es igual o menor a 9.',
      detail: 'La grasa alrededor de los órganos internos se encuentra en niveles saludables y es un buen indicador de salud metabólica. Suele relacionarse con actividad física regular, alimentación balanceada y manejo adecuado del estrés.',
    },
    {
      name: 'Alto',
      range: 'Nivel 10 a 14',
      explanation: 'El nivel de grasa visceral está entre 10 y 14, incluyendo ambos valores.',
      detail: 'Existe un aumento de la grasa que rodea los órganos abdominales. Es más riesgosa que la grasa subcutánea porque se asocia con resistencia a la insulina e inflamación. Puede relacionarse con sedentarismo, exceso de azúcares y grasas saturadas, alcohol, estrés crónico y sueño deficiente.',
    },
    {
      name: 'Muy alto',
      range: 'Nivel 15 o mayor',
      explanation: 'El nivel de grasa visceral es igual o mayor a 15.',
      detail: 'El nivel elevado de grasa visceral se vincula fuertemente con diabetes tipo 2, hipertensión y enfermedad cardiovascular, por lo que constituye una señal de alerta prioritaria. Puede relacionarse con hábitos alimentarios y de actividad física sostenidos, junto con factores hormonales y genéticos.',
    },
  ],
};
const DISEASE_COLORS   = ['#dc3545','#e06c75','#e88f99','#f0b4bb','#f8d7da'];
const LAB_RISK_COLORS  = ['#00be9c','#ffe951','#dc3545'];
const LAB_HDL_COLORS   = ['#dc3545','#ffe951','#00be9c'];
const LAB_LDL_COLORS   = ['#00be9c','#1069f1','#ffe951','#dc3545'];
const TYG_COLORS       = ['#00be9c','#ffe951','#dc3545'];

function getInitials(name) {
  const words = name.trim().split(/\s+/);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}
function getAvatarColor(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// ─── Metadata de cuestionarios ───────────────────────────────────────────────
const SURVEY_META = {
  hra:                     { label: 'HRA',                                           analyticsPath: (id) => `/analytics/hra/${id}`,                    exportFn: (id) => responsesApi.exportHRAResponses(id),      filename: (id) => `hra_responses_${id}.xlsx` },
  healthBurnout:           { label: 'HRA & Burnout',                                 analyticsPath: (id) => `/analytics/hb/${id}`,                     exportFn: (id) => responsesApi.exportHBResponses(id),       filename: (id) => `hb_responses_${id}.xlsx` },
  estresBurnout:           { label: 'Estrés y Burnout',                              analyticsPath: (id) => `/analytics/estresBurnout/${id}`,          exportFn: (id) => responsesApi.exportEstresResponses(id),   filename: (id) => `estres_responses_${id}.xlsx` },
  nom035:                  { label: 'NOM-035',                                        analyticsPath: (id) => `/analytics/nom035/${id}`,                 exportFn: (id) => responsesApi.exportNom035Responses(id),   filename: (id) => `nom035_respuestas_${id}.xlsx` },
  nom035SecI:              { label: 'Nom035 Guía I',                                 analyticsPath: (id) => `/analytics/nom035SecI/${id}`,             exportFn: (id) => responsesApi.exportNom035SecIResponses(id), filename: (id) => `nom035_guia_i_respuestas_${id}.xlsx` },
  generalHealtQuestionarie:{ label: 'Evaluación Integral de Bienestar Emocional',   analyticsPath: (id) => `/analytics/generalhealtquestionarie/${id}`, exportFn: (id) => responsesApi.exportGhqResponses(id),      filename: (id) => `ghq_responses_${id}.xlsx` },
};

const buildAnalyticsPath = (meta, entityId, appId) =>
  appId ? meta.analyticsPath(`${entityId}/${appId}`) : meta.analyticsPath(entityId);

async function downloadBlob(apiFn, filename) {
  const blob = await apiFn();
  const url  = window.URL.createObjectURL(new Blob([blob]));
  const a    = document.createElement('a');
  a.href     = url;
  a.setAttribute('download', filename);
  document.body.appendChild(a);
  a.click();
  a.parentNode.removeChild(a);
  window.URL.revokeObjectURL(url);
}

// ─── Iconos ───────────────────────────────────────────────────────────────────
function PeopleIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}
function SurveyIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <path d="M9 12h6M9 16h4" />
    </svg>
  );
}

// ─── Stacked bar: indicadores metabólicos ────────────────────────────────────
const BAR_INDICATORS = [
  { dataKey: 'glucosa',          label: 'Glucosa',       names: ['Normal', 'Prediabetes', 'Diabetes'] },
  { dataKey: 'trigliceridos',    label: 'Triglicéridos', names: ['Normal', 'Límite alto', 'Alto'] },
  { dataKey: 'colesterol_total', label: 'Colesterol',    names: ['Deseable', 'Límite alto', 'Alto'] },
  { dataKey: 'hba1c',            label: 'HbA1c',         names: ['Normal', 'Prediabetes', 'Diabetes'] },
];
const STACK_COLORS     = LAB_RISK_COLORS;
const TIER_KEYS        = ['tier1', 'tier2', 'tier3'];
const INDICATOR_NAMES  = Object.fromEntries(BAR_INDICATORS.map(({ label, names }) => [label, names]));
const MIN_LABEL_HEIGHT = 22;
const LAB_METABOLIC_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'Cada barra muestra qué porcentaje de resultados se encuentra en cada categoría. Los valores de glucosa, triglicéridos y colesterol se expresan en mg/dL (miligramos por decilitro); la HbA1c (Hemoglobina glucosilada) se expresa como porcentaje.',
  groups: [
    {
      name: 'Glucosa',
      unit: 'mg/dL (miligramos por decilitro)',
      description: 'Concentración de glucosa en ayuno.',
      categories: [
        {
          name: 'Normal',
          range: 'Menor a 100',
          color: STACK_COLORS[0],
          detail: 'Los niveles de azúcar en sangre están dentro del rango saludable y el cuerpo regula bien la glucosa. Suele relacionarse con alimentación equilibrada, actividad física y un peso saludable.',
        },
        {
          name: 'Prediabetes',
          range: '100 a 125',
          color: STACK_COLORS[1],
          detail: 'Los niveles están por encima de lo deseable, pero aún no llegan al rango de diabetes. Es una etapa de alerta y, en muchos casos, reversible. Puede asociarse con resistencia inicial a la insulina, exceso de azúcares y carbohidratos refinados, sobrepeso y sedentarismo.',
        },
        {
          name: 'Diabetes',
          range: '126 o más',
          color: STACK_COLORS[2],
          detail: 'Los niveles son compatibles con diabetes y reflejan una regulación inadecuada de la glucosa. Requieren valoración y seguimiento médico. Pueden asociarse con resistencia a la insulina establecida o déficit de insulina, factores genéticos, obesidad y sedentarismo.',
        },
      ],
    },
    {
      name: 'Triglicéridos',
      unit: 'mg/dL (miligramos por decilitro)',
      description: 'Cantidad de triglicéridos en la sangre.',
      categories: [
        {
          name: 'Normal',
          range: 'Menor a 150',
          color: STACK_COLORS[0],
          detail: 'Las grasas en sangre están en niveles saludables, asociados con menor riesgo cardiovascular. Suele relacionarse con alimentación balanceada, bajo consumo de azúcar y alcohol y actividad física.',
        },
        {
          name: 'Límite alto',
          range: '150 a 199',
          color: STACK_COLORS[1],
          detail: 'Existe una elevación moderada que conviene vigilar porque puede progresar y aumentar el riesgo cardiovascular. Puede asociarse con consumo elevado de azúcares, harinas refinadas y alcohol, además de sedentarismo y sobrepeso.',
        },
        {
          name: 'Alto',
          range: '200 o más',
          color: STACK_COLORS[2],
          detail: 'Los niveles elevados se asocian con mayor riesgo cardiovascular y, cuando son muy altos, con pancreatitis. Pueden relacionarse con exceso de azúcares simples y alcohol, obesidad abdominal, resistencia a la insulina y factores genéticos.',
        },
      ],
    },
    {
      name: 'Colesterol total',
      unit: 'mg/dL (miligramos por decilitro)',
      description: 'Concentración total de colesterol en la sangre.',
      categories: [
        {
          name: 'Deseable',
          range: 'Menor a 200',
          color: STACK_COLORS[0],
          detail: 'El colesterol se encuentra en un rango saludable, asociado con menor riesgo de acumulación en las arterias. Suele relacionarse con una alimentación rica en fibra y grasas saludables y con actividad física.',
        },
        {
          name: 'Límite alto',
          range: '200 a 239',
          color: STACK_COLORS[1],
          detail: 'El nivel está ligeramente elevado y conviene monitorearlo e intervenir mediante hábitos. Puede asociarse con consumo elevado de grasas saturadas y alimentos ultraprocesados, sedentarismo y sobrepeso.',
        },
        {
          name: 'Alto',
          range: '240 o más',
          color: STACK_COLORS[2],
          detail: 'El colesterol elevado incrementa el riesgo de formación de placa en las arterias y de enfermedad cardiovascular. Puede asociarse con una alimentación alta en grasas saturadas y trans, factores genéticos y sedentarismo.',
        },
      ],
    },
    {
      name: 'HbA1c (Hemoglobina glucosilada)',
      unit: '%',
      description: 'Porcentaje de hemoglobina glucosilada.',
      categories: [
        {
          name: 'Normal',
          range: 'Menor a 5.7',
          color: STACK_COLORS[0],
          detail: 'Refleja un buen control del azúcar en sangre durante los últimos dos o tres meses. Suele relacionarse con alimentación equilibrada, actividad física y un peso saludable.',
        },
        {
          name: 'Prediabetes',
          range: '5.7 a 6.4',
          color: STACK_COLORS[1],
          detail: 'El promedio de glucosa de los últimos meses está elevado y representa una señal temprana de riesgo, habitualmente reversible. Puede asociarse con resistencia inicial a la insulina, exceso de azúcares, sobrepeso y sedentarismo.',
        },
        {
          name: 'Diabetes',
          range: '6.5 o más',
          color: STACK_COLORS[2],
          detail: 'Indica un control glucémico deficiente sostenido, compatible con diabetes, y requiere seguimiento médico. Puede asociarse con resistencia a la insulina o déficit de insulina, obesidad y factores genéticos.',
        },
      ],
    },
  ],
};

const METABOLIC_RISK_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'Este indicador es una estimación de tamizaje que integra composición corporal y parámetros de laboratorio; no constituye un diagnóstico. La interpretación individual corresponde siempre al profesional de la salud.',
  categories: [
    {
      name: 'Sin riesgo',
      detail: 'Tanto la composición corporal como la química sanguínea se encuentran en rangos saludables. La estructura y la función del organismo están alineadas y en buen estado, con baja probabilidad de desarrollar enfermedad metabólica en el corto plazo. El objetivo es mantener los hábitos.'
    },
    {
      name: 'Riesgo moderado',
      detail: 'Existen alteraciones tempranas o parciales: una de las dimensiones comienza a mostrar cambios (por ejemplo, grasa visceral elevada con laboratorios aún normales, o valores limítrofes en sangre con composición corporal adecuada), o bien ambas se encuentran en valores limítrofes. Es una etapa de alerta, generalmente reversible con ajustes en alimentación, actividad física y estilo de vida. Es la ventana ideal para la prevención.'
    },
    {
      name: 'Riesgo alto',
      detail: 'Se presentan alteraciones significativas en ambas dimensiones al mismo tiempo, o una alteración marcada en una de ellas (por ejemplo, grasa visceral alta junto con glucosa o lípidos alterados). Este patrón es compatible con síndrome metabólico y con un riesgo cardiometabólico elevado (diabetes tipo 2, hipertensión, enfermedad cardiovascular). Requiere valoración clínica y seguimiento estructurado.'
    }
  ]
}
function buildBarData(labData) {
  if (!labData) return [];
  const pick = (arr, idx) => arr?.[idx]?.value ?? 0;
  return BAR_INDICATORS
    .map(({ dataKey, label }) => {
      const r1 = pick(labData[dataKey], 0);
      const r2 = pick(labData[dataKey], 1);
      const r3 = pick(labData[dataKey], 2);
      const total = r1 + r2 + r3;
      const pct = v => total > 0 ? (v / total) * 100 : 0;
      return { label, count1: r1, count2: r2, count3: r3, tier1: pct(r1), tier2: pct(r2), tier3: pct(r3), total };
    })
    .filter(d => d.total > 0);
}

// ─── Stacked bar secundario: HDL, LDL, Índ. Aterogénico, Índ. TyG ─────────
const SECONDARY_INDICATORS = [
  { labKey: 'hdl',               label: 'HDL',              names: ['Bajo','Moderado','Óptimo'],                    colors: [...LAB_HDL_COLORS, null] },
  { labKey: 'ldl',               label: 'LDL',              names: ['Óptimo','Casi óptimo','Límite alto','Alto'],    colors: LAB_LDL_COLORS },
  { labKey: 'indice_aterogenico',label: 'Índ. Aterogénico', names: ['Bajo riesgo','Riesgo moderado','Alto riesgo'], colors: [...LAB_RISK_COLORS, null] },
  { labKey: 'tyg',               label: 'Índ. TyG',         names: ['Normal','Límite','Elevado'],                   colors: [...TYG_COLORS, null] },
];
const TIER_KEYS4 = ['tier1', 'tier2', 'tier3', 'tier4'];
const LAB_LIPIDS_DETAILS = {
  summary: 'Ver detalle de la clasificación',
  description: 'La gráfica muestra la proporción de personas en cada rango de HDL (Lipoproteína de Alta Densidad), LDL (Lipoproteína de Baja Densidad), índice aterogénico e índice TyG (Triglicéridos-Glucosa).',
  groups: [
    {
      name: 'HDL (Lipoproteína de Alta Densidad)',
      unit: 'mg/dL (miligramos por decilitro)',
      description: 'En HDL (Lipoproteína de Alta Densidad), un valor mayor corresponde a una mejor categoría. Los límites bajo y moderado son diferentes para hombres y mujeres.',
      categories: [
        {
          name: 'Bajo',
          range: 'Hombres: <40 · Mujeres: <50',
          color: LAB_HDL_COLORS[0],
          detail: 'Existe poca cantidad de colesterol protector, lo que deja las arterias más expuestas y aumenta el riesgo cardiovascular. Puede asociarse con sedentarismo, tabaquismo, sobrepeso, alimentación pobre en grasas saludables y exceso de azúcares.',
        },
        {
          name: 'Moderado',
          range: 'Hombres: 40–59 · Mujeres: 50–59',
          color: LAB_HDL_COLORS[1],
          detail: 'Es un nivel intermedio que ofrece cierta protección, aunque todavía existe margen de mejora. Conviene reforzar la actividad física y una alimentación equilibrada.',
        },
        {
          name: 'Óptimo',
          range: '60 o más',
          color: LAB_HDL_COLORS[2],
          detail: 'Representa una buena cantidad de colesterol protector y se asocia con menor riesgo cardiovascular. Suele relacionarse con actividad física regular, consumo de grasas saludables y ausencia de tabaquismo.',
        },
      ],
    },
    {
      name: 'LDL (Lipoproteína de Baja Densidad)',
      unit: 'mg/dL (miligramos por decilitro)',
      description: 'En LDL (Lipoproteína de Baja Densidad), los valores menores corresponden a mejores categorías.',
      categories: [
        {
          name: 'Óptimo',
          range: 'Menor a 100',
          color: LAB_LDL_COLORS[0],
          detail: 'Es un nivel bajo del colesterol que tiende a depositarse en las arterias y representa un escenario favorable para la salud cardiovascular. Suele relacionarse con alimentación rica en fibra y grasas saludables, actividad física y un peso saludable.',
        },
        {
          name: 'Casi óptimo',
          range: '100 a 129',
          color: LAB_LDL_COLORS[1],
          detail: 'Está ligeramente por encima del nivel ideal, pero todavía refleja buen control. Es un momento adecuado para reforzar hábitos. Puede asociarse con consumo moderado de grasas saturadas y actividad física insuficiente.',
        },
        {
          name: 'Límite alto',
          range: '130 a 159',
          color: LAB_LDL_COLORS[2],
          detail: 'El nivel comienza a aumentar el riesgo cardiovascular y conviene intervenir con cambios de estilo de vida. Puede asociarse con alimentación alta en grasas saturadas y alimentos ultraprocesados, sedentarismo y sobrepeso.',
        },
        {
          name: 'Alto',
          range: '160 o más',
          color: LAB_LDL_COLORS[3],
          detail: 'El nivel elevado favorece la formación de placa en las arterias y aumenta significativamente el riesgo cardiovascular. Puede asociarse con grasas saturadas y trans, factores genéticos y sedentarismo.',
        },
      ],
    },
    {
      name: 'Índice aterogénico',
      unit: 'Colesterol total / HDL (Lipoproteína de Alta Densidad)',
      description: 'Relación calculada entre el colesterol total y el HDL (Lipoproteína de Alta Densidad).',
      categories: [
        { 
          name: 'Bajo riesgo', 
          range: 'Menor a 3.5', 
          color: LAB_RISK_COLORS[0],
          detail: 'El índice aterogénico está en un rango saludable, lo que indica menor riesgo de acumulación de placa en las arterias. Suele relacionarse con niveles adecuados de colesterol total y HDL, actividad física y alimentación balanceada.',
        },
        { 
          name: 'Riesgo moderado', 
          range: '3.5 a 5.0', 
          color: LAB_RISK_COLORS[1], 
          detail: 'El índice aterogénico está en un rango moderado, lo que indica un riesgo intermedio de acumulación de placa en las arterias. Puede asociarse con factores de riesgo modifiables como la alimentación y el estilo de vida.' 
        },
        { 
          name: 'Alto riesgo', 
          range: 'Mayor a 5.0', 
          color: LAB_RISK_COLORS[2], 
          detail: 'El índice aterogénico está en un rango alto, lo que indica un elevado riesgo de acumulación de placa en las arterias. Es importante intervenir con cambios significativos en el estilo de vida y posiblemente tratamiento farmacológico.' 
        },
      ],
    },
    {
      name: 'Índice TyG (Triglicéridos-Glucosa)',
      unit: 'Índice calculado',
      description: 'Se calcula con glucosa y triglicéridos: ln (logaritmo natural)[(triglicéridos × glucosa) / 2].',
      categories: [
        { 
          name: 'Normal', 
          range: 'Menor a 8.5', 
          color: TYG_COLORS[0],
          detail: 'El índice TyG está en un rango saludable, lo que indica un buen control de la glucosa y los triglicéridos. Suele relacionarse con alimentación equilibrada, actividad física y un peso saludable.',               
        },
        { 
          name: 'Límite', 
          range: '8.5 a <9.0', 
          color: TYG_COLORS[1],
          detail: 'El índice TyG está en un rango límite, lo que indica un control moderado de la glucosa y los triglicéridos. Es importante mantener hábitos saludables para evitar el progreso hacia rangos más elevados.' 
        },
        { 
          name: 'Elevado', 
          range: '9.0 o más', 
          color: TYG_COLORS[2],
          detail: 'El índice TyG está en un rango elevado, lo que indica un control deficiente de la glucosa y los triglicéridos. Es fundamental implementar cambios significativos en el estilo de vida y considerar intervención médica.' 
        },
      ],
    },
  ],
};

function buildSecondaryBarData(labData, tygData) {
  const pick = (arr, idx) => arr?.[idx]?.value ?? 0;
  return SECONDARY_INDICATORS
    .map(({ labKey, label, names, colors }) => {
      const src = labKey === 'tyg' ? tygData : labData?.[labKey];
      const r1 = pick(src, 0);
      const r2 = pick(src, 1);
      const r3 = pick(src, 2);
      const r4 = labKey === 'ldl' ? pick(src, 3) : 0;
      const total = r1 + r2 + r3 + r4;
      const pct = v => total > 0 ? (v / total) * 100 : 0;
      return { label, names, colors, count1: r1, count2: r2, count3: r3, count4: r4, tier1: pct(r1), tier2: pct(r2), tier3: pct(r3), tier4: pct(r4), total };
    })
    .filter(d => d.total > 0);
}

function makeBarShape(fill, dataKey) {
  return function BarShape(props) {
    const { x, y, width, height } = props;
    const value = props[dataKey];
    const rect = <rect x={x} y={y} width={width} height={Math.max(height, 0)} fill={fill} />;
    if (!value || value <= 0 || height <= 0) return rect;
    const label = `${Math.round(value)}%`;
    const cx = x + width / 2;
    const cy = y + height / 2;
    if (height >= MIN_LABEL_HEIGHT) {
      return (
        <g>
          {rect}
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
            fontSize={11} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
            {label}
          </text>
        </g>
      );
    }
    const lineX1 = x + width + 2;
    const lineX2 = x + width + 20;
    return (
      <g>
        {rect}
        <line x1={lineX1} y1={cy} x2={lineX2} y2={cy}
          stroke="#9aabba" strokeWidth={1} strokeDasharray="3 2" />
        <text x={lineX2 + 3} y={cy} textAnchor="start" dominantBaseline="central"
          fontSize={10} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
          {label}
        </text>
      </g>
    );
  };
}

function makeBarShape2(dataKey, tierIdx) {
  return function BarShape(props) {
    const { x, y, width, height } = props;
    const fill  = props.colors?.[tierIdx] ?? 'transparent';
    const value = props[dataKey];
    const rect  = <rect x={x} y={y} width={width} height={Math.max(height, 0)} fill={fill} />;
    if (!value || value <= 0 || height <= 0) return rect;
    const label = `${Math.round(value)}%`;
    const cx = x + width / 2;
    const cy = y + height / 2;
    if (height >= MIN_LABEL_HEIGHT) {
      return (
        <g>
          {rect}
          <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central"
            fontSize={11} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
            {label}
          </text>
        </g>
      );
    }
    const lineX1 = x + width + 2;
    const lineX2 = x + width + 20;
    return (
      <g>
        {rect}
        <line x1={lineX1} y1={cy} x2={lineX2} y2={cy}
          stroke="#9aabba" strokeWidth={1} strokeDasharray="3 2" />
        <text x={lineX2 + 3} y={cy} textAnchor="start" dominantBaseline="central"
          fontSize={10} fontWeight={700} fill="#042330" fontFamily="Montserrat, sans-serif">
          {label}
        </text>
      </g>
    );
  };
}

function SecondaryTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;
  if (!row) return null;
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{label}</span>
      {TIER_KEYS4.map((key, i) => {
        const count = row[`count${i + 1}`];
        const name  = row.names?.[i];
        if (!count || !name) return null;
        return (
          <span key={key} style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: row.colors?.[i] ?? '#042330' }}>
            {name}: {count}
          </span>
        );
      })}
    </div>
  );
}

function StackedSecondaryChart({ labData, tygData, loading }) {
  if (loading) return null;
  const data = buildSecondaryBarData(labData, tygData);
  if (!data.length) return null;

  return (
    <div className={styles.chartCard} style={{ marginTop: '1rem' }}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>Lípidos e Índices Metabólicos</h3>
        <span className={styles.chartTotal}>
          {data[0].count1 + data[0].count2 + data[0].count3 + data[0].count4}
        </span>
        <DownloadChartButton title="Lípidos e Índices Metabólicos" />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} barSize={80} margin={{ top: 16, right: 48, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf2" />
          <XAxis dataKey="label"
            tick={{ fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat, sans-serif', fill: '#042330' }}
            axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`}
            tick={{ fontSize: 10, fill: '#76797c', fontFamily: 'Montserrat, sans-serif' }}
            axisLine={false} tickLine={false} width={36} />
          <Tooltip content={<SecondaryTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          {TIER_KEYS4.map((key, i) => (
            <Bar key={key} dataKey={key} stackId="a"
              radius={i === TIER_KEYS4.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
              shape={makeBarShape2(key, i)} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <ChartDetails details={LAB_LIPIDS_DETAILS} />
    </div>
  );
}

function BarChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const names = INDICATOR_NAMES[label] ?? ['Normal', 'Límite', 'Alto'];
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{label}</span>
      {[...payload].reverse().map((p) => {
        const tierIndex = TIER_KEYS.indexOf(p.dataKey);
        const count = p.payload[`count${tierIndex + 1}`];
        if (!count) return null;
        return (
          <span key={p.dataKey} style={{ display: 'block', fontSize: '0.8rem', fontWeight: 700, color: STACK_COLORS[tierIndex] }}>
            {names[tierIndex]}: {count}
          </span>
        );
      })}
    </div>
  );
}

function StackedLabChart({ labData, loading }) {
  if (loading) return null;
  const data = buildBarData(labData);
  if (!data.length) return null;

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>Indicadores Metabólicos</h3>
        <span className={styles.chartTotal}>
          {data[0].count1 + data[0].count2 + data[0].count3}
        </span>
        <DownloadChartButton title="Indicadores Metabólicos" />
      </div>
      <ResponsiveContainer width="100%" height={320}>
        <BarChart data={data} barSize={80} margin={{ top: 16, right: 48, bottom: 4, left: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e8edf2" />
          <XAxis dataKey="label"
            tick={{ fontSize: 11, fontWeight: 600, fontFamily: 'Montserrat, sans-serif', fill: '#042330' }}
            axisLine={false} tickLine={false} />
          <YAxis domain={[0, 100]} tickFormatter={v => `${v}%`}
            tick={{ fontSize: 10, fill: '#76797c', fontFamily: 'Montserrat, sans-serif' }}
            axisLine={false} tickLine={false} width={36} />
          <Tooltip content={<BarChartTooltip />} cursor={{ fill: 'rgba(0,0,0,0.04)' }} />
          <Legend iconType="circle" iconSize={8}
            wrapperStyle={{ fontSize: '0.68rem', paddingTop: '8px' }}
            payload={[
              { value: 'Normal / Deseable',    type: 'circle', color: STACK_COLORS[0] },
              { value: 'Límite / Prediabetes', type: 'circle', color: STACK_COLORS[1] },
              { value: 'Alto / Diabetes',      type: 'circle', color: STACK_COLORS[2] },
            ]} />
          {TIER_KEYS.map((key, i) => (
            <Bar key={key} dataKey={key} stackId="a"
              radius={i === TIER_KEYS.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
              shape={makeBarShape(STACK_COLORS[i], key)} />
          ))}
        </BarChart>
      </ResponsiveContainer>
      <ChartDetails details={LAB_METABOLIC_DETAILS} />
    </div>
  );
}

// ─── Pie label ────────────────────────────────────────────────────────────────
const RADIAN = Math.PI / 180;
function PieLabel({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) {
  if (!value) return null;
  if (percent >= 0.07) {
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    return (
      <text x={cx + r * Math.cos(-midAngle * RADIAN)} y={cy + r * Math.sin(-midAngle * RADIAN)}
        fill="#042330" textAnchor="middle" dominantBaseline="central"
        fontSize={11} fontWeight={700} fontFamily="Montserrat, sans-serif">
        {value}
      </text>
    );
  }
  const startR = outerRadius + 6, endR = outerRadius + 28;
  const x1 = cx + startR * Math.cos(-midAngle * RADIAN), y1 = cy + startR * Math.sin(-midAngle * RADIAN);
  const x2 = cx + endR * Math.cos(-midAngle * RADIAN),   y2 = cy + endR * Math.sin(-midAngle * RADIAN);
  const isRight = x2 > cx, tickEndX = x2 + (isRight ? 8 : -8);
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#9aabba" strokeWidth={1} strokeDasharray="3 2" />
      <line x1={x2} y1={y2} x2={tickEndX} y2={y2} stroke="#9aabba" strokeWidth={1} />
      <text x={tickEndX + (isRight ? 3 : -3)} y={y2} fill="#042330"
        textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central"
        fontSize={10} fontWeight={700} fontFamily="Montserrat, sans-serif">{value}</text>
    </g>
  );
}

function CustomTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{payload[0].name}</span>
      <span className={styles.tooltipValue}>{payload[0].value} respuestas</span>
    </div>
  );
}

// ─── Surveys table ────────────────────────────────────────────────────────────
// entities: array de objetos { _id, surveys[] }
// En modo General: todas las branches. En modo sucursal: [selectedBranch].
export function SurveysTable({ entities, counts, applicationCounts, loading, navigate,
  nomOnly = false, title = 'Cuestionarios de Bienestar', headerActions = null,
}) {
  const rows = (entities ?? []).flatMap(entity =>
    (entity?.surveys ?? [])
      .filter(s => s.active && ['nom035', 'nom035SecI'].includes(s.name) === nomOnly)
      .flatMap(survey => {
        const meta = SURVEY_META[survey.name];
        if (!meta) return [];
        const entityId = String(entity._id);
        const apps = survey.applications ?? [];
        if (!apps.length) {
          return [{
            key: `${entityId}-${survey.name}`,
            entityId,
            meta,
            applicationName: null,
            appId: null,
            surveyUrl: survey.surveyUrl,
          }];
        }
        return apps.map(app => ({
          key: String(app.questionnaireApplicationId),
          entityId,
          meta,
          applicationName: app.applicationName,
          appId: String(app.questionnaireApplicationId),
          surveyUrl: app.surveyUrl,
        }));
      })
  );

  if (!rows.length) return null;

  return (
    <div className={styles.surveysCard}>
      <div className={styles.surveysHeader}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6f42c1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
          <rect x="9" y="2" width="6" height="4" rx="1" />
          <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
          <path d="M9 12h6M9 16h4" />
        </svg>
        <h3 className={styles.surveysTitle}>{title}</h3>
        {headerActions}
      </div>

      <table className={styles.surveysTable}>
        <thead>
          <tr>
            <th className={styles.th}>Cuestionario</th>
            <th className={styles.th}>Aplicación</th>
            <th className={styles.th}>Respuestas</th>
            <th className={styles.th}>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const responseCount = row.appId != null
              ? (applicationCounts?.[row.appId] ?? 0)
              : (counts?.[row.meta.label] ?? counts?.[row.meta.key] ?? '—');

            return (
              <tr key={row.key} className={styles.tr}>
                <td className={styles.tdName}>{row.meta.label}</td>
                <td className={styles.tdName}>{row.applicationName ?? '—'}</td>
                <td className={styles.tdCount}>
                  {loading ? <span className={styles.skeletonSm} /> : responseCount}
                </td>
                <td className={styles.tdActions}>
                  <button className={styles.actionBtn}
                    onClick={() => navigate(buildAnalyticsPath(row.meta, row.entityId, row.appId))}>
                    Ver gráficas
                  </button>
                  <button className={styles.actionBtn}
                    onClick={() => downloadBlob(() => row.meta.exportFn(row.entityId), row.meta.filename(row.entityId))}>
                    Descargar
                  </button>
                  <button className={styles.actionBtn}
                    onClick={() => { if (row.surveyUrl) navigator.clipboard.writeText(row.surveyUrl); }}>
                    Copiar URL
                  </button>
                  <button className={styles.actionBtn}
                    onClick={() => row.surveyUrl && window.open(row.surveyUrl, '_blank', 'noopener,noreferrer')}>
                    Abrir
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function Nom035ApplicationsSection({ entities, company, showCompanyTotals = true, ...tableProps }) {
  const [showGroupedModal, setShowGroupedModal] = useState(false);
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  const groups = entities.filter(entity => entity.surveys?.some(
    survey => survey.active && ['nom035', 'nom035SecI'].includes(survey.name)
  ));
  if (!groups.length) return null;

  const nom035Applications = groups.flatMap(entity => {
    const survey = entity.surveys?.find(item => item.active && item.name === 'nom035');
    return (survey?.applications ?? []).map(application => ({
      id: String(application.questionnaireApplicationId),
      name: application.applicationName || 'Aplicación sin nombre',
      entityName: entity.name || company?.name || 'Empresa',
    }));
  });
  const canGroupApplications = nom035Applications.length >= 2
    && (showCompanyTotals || groups.length === 1);

  const openGroupedModal = () => {
    setSelectedApplicationIds([]);
    setShowGroupedModal(true);
  };

  const toggleGroupedApplication = (applicationId) => {
    setSelectedApplicationIds(current => current.includes(applicationId)
      ? current.filter(id => id !== applicationId)
      : [...current, applicationId]);
  };

  const viewGroupedResults = () => {
    if (selectedApplicationIds.length < 2) return;

    const useCompanyScope = showCompanyTotals && company?._id;
    const targetId = useCompanyScope ? company._id : groups[0]._id;
    const targetScope = useCompanyScope
      ? 'company'
      : String(targetId) === String(company?._id) ? 'company' : 'branch';
    const query = new URLSearchParams({
      applicationIds: selectedApplicationIds.join(','),
    });
    tableProps.navigate(`/analytics/nom035/${targetScope}/${targetId}?${query.toString()}`);
  };

  const totalButtons = (groupEntities, id, scope, companyTotal = false) => (
    <div className={styles.nomTotalActions}>
      {['nom035', 'nom035SecI'].filter(type => groupEntities.some(entity =>
        entity.surveys?.some(survey => survey.active && survey.name === type)
      )).map(type => (
        <button key={type} type="button" className={styles.actionBtn}
          onClick={() => tableProps.navigate(`/analytics/${type}/${scope}/${id}`)}>
          {companyTotal ? 'Ver gráficas de toda la compañía' : 'Ver gráficas totales'}
          {type === 'nom035SecI' ? ' · Guía I' : ' · NOM-035'}
        </button>
      ))}
    </div>
  );

  return (
    <section className={styles.section} aria-label="Aplicaciones NOM-035">
      <div className={styles.nomSectionHeader}>
        <h2 className={styles.sectionTitle}>Aplicaciones NOM-035</h2>
        <div className={styles.nomTotalActions}>
          {nom035Applications.length > 0 && (
            <button type="button" className={styles.groupedResultsButton}
              disabled={!canGroupApplications}
              title={canGroupApplications
                ? 'Combinar aplicaciones NOM-035'
                : 'Se necesitan al menos dos aplicaciones NOM-035 del alcance actual'}
              onClick={openGroupedModal}>
              Visualizar resultados agrupados
            </button>
          )}
          {showCompanyTotals && company?._id && totalButtons(groups, company._id, 'company', true)}
        </div>
      </div>
      <div className={styles.nomBranchGroups}>
        {groups.map(entity => (
          <SurveysTable key={entity._id} {...tableProps} entities={[entity]} nomOnly
            title={entity.name || 'Sin sucursal'}
            headerActions={totalButtons([entity], entity._id,
              String(entity._id) === String(company?._id) ? 'company' : 'branch')}
          />
        ))}
      </div>

      {showGroupedModal && (
        <div className={styles.modalBackdrop} role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setShowGroupedModal(false);
          }}>
          <div className={styles.groupedModal} role="dialog" aria-modal="true"
            aria-labelledby="grouped-results-title">
            <div className={styles.groupedModalHeader}>
              <div>
                <span className={styles.groupedModalEyebrow}>NOM-035</span>
                <h3 id="grouped-results-title">Visualizar resultados agrupados</h3>
              </div>
              <button type="button" className={styles.modalCloseButton}
                aria-label="Cerrar" onClick={() => setShowGroupedModal(false)}>×</button>
            </div>
            <p className={styles.groupedModalDescription}>
              Selecciona al menos dos aplicaciones para combinar sus resultados en Analytics.
            </p>
            <div className={styles.applicationChecklist}>
              {nom035Applications.map(application => (
                <label key={application.id} className={styles.applicationCheckRow}>
                  <input type="checkbox"
                    checked={selectedApplicationIds.includes(application.id)}
                    onChange={() => toggleGroupedApplication(application.id)} />
                  <span>
                    <strong>{application.name}</strong>
                    <small>{application.entityName}</small>
                  </span>
                </label>
              ))}
            </div>
            <div className={styles.groupedModalFooter}>
              <span>{selectedApplicationIds.length} aplicaciones seleccionadas</span>
              <div>
                <button type="button" className={styles.modalCancelButton}
                  onClick={() => setShowGroupedModal(false)}>Cancelar</button>
                <button type="button" className={styles.modalContinueButton}
                  disabled={selectedApplicationIds.length < 2}
                  onClick={viewGroupedResults}>Continuar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function StatCard({ icon, value, label, loading, iconColor }) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statIcon} style={{ color: iconColor }}>{icon}</div>
      <div className={styles.statValue}>
        {loading ? <span className={styles.skeleton} /> : value}
      </div>
      <div className={styles.statLabel}>{label}</div>
    </div>
  );
}

function SexTabs({ active, onChange }) {
  return (
    <div className={styles.tabs}>
      {['global', 'masculino', 'femenino'].map(t => (
        <button key={t} className={`${styles.tab} ${active === t ? styles.tabActive : ''}`}
          onClick={() => onChange(t)}>
          {t.charAt(0).toUpperCase() + t.slice(1)}
        </button>
      ))}
    </div>
  );
}

const MONTH_NAMES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

function deduplicatePeriods(records) {
  const seen = new Set();
  const result = [];
  for (const r of records) {
    if (!r.period?.month || !r.period?.year) continue;
    const key = `${r.period.month}-${r.period.year}`;
    if (!seen.has(key)) {
      seen.add(key);
      result.push({ month: r.period.month, year: r.period.year });
    }
  }
  return result.sort((a, b) => b.year !== a.year ? b.year - a.year : b.month - a.month);
}

function PeriodDropdown({ periods, value, onChange }) {
  if (!periods.length) return null;
  const key = (p) => p ? `${p.month}-${p.year}` : '';
  return (
    <select
      className={styles.periodSelect}
      value={key(value)}
      onChange={e => {
        if (!e.target.value) { onChange(null); return; }
        const [m, y] = e.target.value.split('-').map(Number);
        onChange({ month: m, year: y });
      }}
    >
      <option value="">Todos los períodos</option>
      {periods.map(p => (
        <option key={key(p)} value={key(p)}>
          {MONTH_NAMES[p.month - 1]} {p.year}
        </option>
      ))}
    </select>
  );
}

function ChartDetails({ details, colors }) {
  if (!details) return null;

  return (
    <details className={styles.chartDetails}>
      <summary className={styles.chartDetailsSummary}>
        <span>{details.summary}</span>
        <svg className={styles.chartDetailsChevron} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <div className={styles.chartDetailsContent}>
        <p className={styles.chartDetailsDescription}>{details.description}</p>
        {details.highlight && <p className={styles.chartDetailsHighlight}>{details.highlight}</p>}
        {details.groups ? (
          <div className={styles.chartDetailsGroups}>
            {details.groups.map(group => (
              <section key={group.name} className={styles.chartDetailsGroup}>
                <div className={styles.chartDetailsGroupHeader}>
                  <h4>{group.name}</h4>
                  <span>{group.unit}</span>
                </div>
                <p className={styles.chartDetailsGroupDescription}>{group.description}</p>
                <ul className={styles.chartDetailsRanges}>
                  {group.categories.map(category => (
                    <li key={category.name}>
                      <span
                        className={styles.chartDetailsDot}
                        style={{ backgroundColor: category.color }}
                        aria-hidden="true"
                      />
                      <strong>{category.name}</strong>
                      <span className={styles.chartDetailsRangeValue}>{category.range}</span>
                      {category.detail && (
                        <p className={styles.chartDetailsRangeDetail}>{category.detail}</p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <ul className={styles.chartDetailsList}>
            {details.categories.map((category, index) => (
              <li key={category.name} className={styles.chartDetailsItem}>
                <span
                  className={styles.chartDetailsDot}
                  style={{ backgroundColor: colors[index % colors.length] }}
                  aria-hidden="true"
                />
                <div>
                  <div className={styles.chartDetailsCategory}>
                    <strong>{category.name}</strong>
                    <span>{category.range}</span>
                  </div>
                  <p>{category.explanation}</p>
                  {category.detail && (
                    <p className={styles.chartDetailsExtended}>{category.detail}</p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </details>
  );
}

function DonutChart({ title, data, colors, loading, startAngle = 0, details = null }) {
  const hasData = data?.some(d => d.value > 0);
  const total = data?.reduce((sum, d) => sum + (d.value || 0), 0) ?? 0;
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>{title}</h3>
        {!loading && hasData && <span className={styles.chartTotal}>{total}</span>}
        {!loading && hasData && <DownloadChartButton title={title} />}
      </div>
      {loading ? <div className={styles.chartSkeleton} /> :
       !hasData ? <div className={styles.chartEmpty}>Sin datos disponibles</div> : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <Pie data={data} cx="50%" cy="45%" innerRadius={48} outerRadius={74}
              paddingAngle={2} dataKey="value"
              startAngle={startAngle} endAngle={startAngle + 360}
              label={PieLabel} labelLine={false}>
              {data.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
            </Pie>
            <Tooltip content={<CustomTooltip />} />
            <Legend iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize: '0.68rem', paddingTop: '6px' }} />
          </PieChart>
        </ResponsiveContainer>
      )}
      <ChartDetails details={details} colors={colors} />
    </div>
  );
}

function PiePctLabel({ cx, cy, midAngle, innerRadius, outerRadius, value, percent }) {
  if (!value) return null;
  const display = `${Math.round(percent * 100)}%`;
  if (percent >= 0.07) {
    const r = innerRadius + (outerRadius - innerRadius) * 0.5;
    return (
      <text x={cx + r * Math.cos(-midAngle * RADIAN)} y={cy + r * Math.sin(-midAngle * RADIAN)}
        fill="#042330" textAnchor="middle" dominantBaseline="central"
        fontSize={11} fontWeight={700} fontFamily="Montserrat, sans-serif">
        {display}
      </text>
    );
  }
  const startR = outerRadius + 6, endR = outerRadius + 28;
  const x1 = cx + startR * Math.cos(-midAngle * RADIAN), y1 = cy + startR * Math.sin(-midAngle * RADIAN);
  const x2 = cx + endR * Math.cos(-midAngle * RADIAN),   y2 = cy + endR * Math.sin(-midAngle * RADIAN);
  const isRight = x2 > cx, tickEndX = x2 + (isRight ? 8 : -8);
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#9aabba" strokeWidth={1} strokeDasharray="3 2" />
      <line x1={x2} y1={y2} x2={tickEndX} y2={y2} stroke="#9aabba" strokeWidth={1} />
      <text x={tickEndX + (isRight ? 3 : -3)} y={y2} fill="#042330"
        textAnchor={isRight ? 'start' : 'end'} dominantBaseline="central"
        fontSize={10} fontWeight={700} fontFamily="Montserrat, sans-serif">{display}</text>
    </g>
  );
}

function MetabolicRiskTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  return (
    <div className={styles.tooltip}>
      <span className={styles.tooltipName}>{payload[0].name}</span>
      <span className={styles.tooltipValue}>{payload[0].value} personas</span>
    </div>
  );
}

const MR_COLORS = ['#00be9c', '#ffe951', '#dc3545'];

function formatPeriodList(periods) {
  if (!periods?.length) return '';
  return periods
    .map(p => `${MONTH_NAMES[p.month - 1].slice(0, 3)} ${p.year}`)
    .join(', ');
}

function MetabolicRiskDonutChart({ data, loading, ibPeriods, labPeriods, details=null }) {
  const hasData = data?.some(d => d.value > 0);
  const total   = data?.reduce((sum, d) => sum + (d.value || 0), 0) ?? 0;

  const ibLabel  = formatPeriodList(ibPeriods);
  const labLabel = formatPeriodList(labPeriods);
  const title = ibLabel || labLabel
    ? `Riesgo Metabólico — cruce InBody${ibLabel ? ` [${ibLabel}]` : ''} × QS (Química Sanguínea)${labLabel ? ` [${labLabel}]` : ''}`
    : 'Riesgo Metabólico — cruce InBody × QS (Química Sanguínea)';

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>{title}</h3>
        {!loading && hasData && <span className={styles.chartTotal}>{total}</span>}
        {!loading && hasData && <DownloadChartButton title={title} />}
      </div>
      {loading ? <div className={styles.chartSkeleton} /> :
       !hasData ? <div className={styles.chartEmpty}>Sin datos disponibles</div> : (
        <ResponsiveContainer width="100%" height={220}>
          <PieChart margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <Pie data={data} cx="50%" cy="45%" innerRadius={48} outerRadius={74}
              paddingAngle={2} dataKey="value"
              label={PiePctLabel} labelLine={false}>
              {data.map((_, i) => <Cell key={i} fill={MR_COLORS[i % MR_COLORS.length]} />)}
            </Pie>
            <Tooltip content={<MetabolicRiskTooltip />} />
            <Legend iconType="circle" iconSize={8}
              wrapperStyle={{ fontSize: '0.68rem', paddingTop: '6px' }} />
          </PieChart>
        </ResponsiveContainer>
      )}
      <ChartDetails details={details} colors={MR_COLORS} />
    </div>
  );
}

function TopDiseases({ data, loading }) {
  const max = data?.[0]?.count || 1;
  return (
    <div className={styles.chartCard}>
      <div className={styles.chartTitleRow}>
        <h3 className={styles.chartTitle}>Top Riesgos Globales</h3>
        {!loading && data?.length > 0 && <DownloadChartButton title="Top Riesgos Globales" />}
      </div>
      {loading ? <div className={styles.chartSkeleton} /> :
       !data?.length ? <div className={styles.chartEmpty}>Sin datos disponibles</div> : (
        <ul className={styles.diseaseList}>
          {data.map((d, i) => (
            <li key={d.name} className={styles.diseaseItem}>
              <div className={styles.diseaseHeader}>
                <span className={styles.diseaseDot} style={{ background: DISEASE_COLORS[i] }} />
                <span className={styles.diseaseName} title={d.name}>{d.name}</span>
                <span className={styles.diseaseCount}>{d.count}</span>
              </div>
              <div className={styles.diseaseBar}>
                <div className={styles.diseaseBarFill}
                  style={{ width: `${Math.round((d.count / max) * 100)}%`, background: DISEASE_COLORS[i] }} />
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────
export default function CompanyDashboardPage({
  activeBranchScope = false,
  showSidebar = true,
}) {
  const { companyId: routeCompanyId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  let tokenBranchId = null;
  let tokenCompanyId = null;
  let canViewCompanyNomTotals = true;
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const decoded = jwtDecode(token);
      tokenBranchId = decoded.branches?.[0] ?? null;
      canViewCompanyNomTotals = ['super admin', 'root user'].includes(decoded.role)
        || !decoded.branches?.length;
      tokenCompanyId = decoded.companies?.[0] ?? null;
    }
  } catch (_) {}

  // /dashboard conserva el selector de sucursal del Header. La ruta
  // /:companyId/dashboard continúa trabajando con el selector de esta página.
  const activeBranchId = activeBranchScope
    ? (location.state?.branchId ?? tokenBranchId)
    : null;
  const requestedCompanyId = routeCompanyId ?? (
    activeBranchScope && !activeBranchId
      ? (location.state?.companyId ?? tokenCompanyId)
      : null
  );

  const [company,       setCompany]       = useState(null);
  const [branches,      setBranches]      = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [employeeCount, setEmployeeCount] = useState(null);
  const [loadingBase,   setLoadingBase]   = useState(true);

  // HRA
  const [hraMetabolic, setHraMetabolic] = useState(null);
  const [hraImc,       setHraImc]       = useState(null);
  const [hraDiseases,  setHraDiseases]  = useState([]);
  const [loadingHra,   setLoadingHra]   = useState(true);
  const [sexTabHra,    setSexTabHra]    = useState('global');

  // InBody
  const [ibBmi,        setIbBmi]        = useState(null);
  const [ibPbf,        setIbPbf]        = useState(null);
  const [ibVfl,        setIbVfl]        = useState(null);
  const [loadingIb,    setLoadingIb]    = useState(true);
  const [sexTabIb,     setSexTabIb]     = useState('global');
  const [ibPeriods,    setIbPeriods]    = useState([]);
  const [selectedIbPeriod, setSelectedIbPeriod] = useState(null);

  // Lab results
  const [labData,      setLabData]      = useState(null);
  const [tygData,      setTygData]      = useState(null);
  const [loadingLab,   setLoadingLab]   = useState(true);
  const [labPeriods,   setLabPeriods]   = useState([]);
  const [selectedLabPeriod, setSelectedLabPeriod] = useState(null);

  // Riesgo metabólico real
  const [mrData,         setMrData]         = useState(null);
  const [loadingMr,      setLoadingMr]      = useState(true);
  const [availableCrosses, setAvailableCrosses] = useState([]);
  const [selectedCross,    setSelectedCross]    = useState(null);

  // Survey counts
  const [surveyCounts,      setSurveyCounts]      = useState(null);
  const [applicationCounts, setApplicationCounts] = useState({});
  const [loadingCounts,     setLoadingCounts]     = useState(true);

  // ── Valores derivados ────────────────────────────────────────────────────────
  // scopeId siempre refleja la sucursal elegida en el Header o en el selector local.
  const scopeId = activeBranchId
    ? String(activeBranchId)
    : selectedBranch
      ? String(selectedBranch._id)
      : requestedCompanyId;
  const isBranchScope = Boolean(activeBranchId || selectedBranch);

  // En modo General revisamos todas las branches (o company como fallback).
  // En modo sucursal revisamos solo la branch seleccionada.
  const showHra = !loadingBase && (
    selectedBranch
      ? selectedBranch.surveys?.some(s => s.name === 'hra' && s.active) === true
      : branches.some(b => b.surveys?.some(s => s.name === 'hra' && s.active))
        || company?.surveys?.some(s => s.name === 'hra' && s.active) === true
  );
  const showInbody = !loadingBase && (
    selectedBranch
      ? selectedBranch.hasInbodys === true
      : branches.some(b => b.hasInbodys === true) || company?.hasInbodys === true
  );

  // ── Carga base (company + branches) ─────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoadingBase(true);
    setSelectedBranch(null);

    const loadBase = async () => {
      try {
        if (activeBranchScope && activeBranchId) {
          const branch = await companyBranchesApi.getById(activeBranchId);
          const parentCompanyId = branch.companyId;
          const comp = await companiesApi.getCompanyById(parentCompanyId);
          if (cancelled) return;
          setCompany(comp);
          setBranches([branch]);
          setSelectedBranch(branch);
          return;
        }

        if (!requestedCompanyId) {
          if (!cancelled) {
            setCompany(null);
            setBranches([]);
          }
          return;
        }

        const [comp, branchList] = await Promise.all([
          companiesApi.getCompanyById(requestedCompanyId),
          activeBranchScope
            ? Promise.resolve([])
            : companyBranchesApi.getByCompany(requestedCompanyId),
        ]);
        if (cancelled) return;
        setCompany(comp);
        setBranches(branchList ?? []);
      } catch (err) {
        if (!cancelled) console.error('Error cargando empresa:', err);
      } finally {
        if (!cancelled) setLoadingBase(false);
      }
    };

    loadBase();
    return () => { cancelled = true; };
  }, [activeBranchScope, activeBranchId, requestedCompanyId]);

  // ── Reset de períodos al cambiar scope ───────────────────────────────────────
  useEffect(() => {
    setSelectedIbPeriod(null);
    setSelectedLabPeriod(null);
    setSelectedCross(null);
    setIbPeriods([]);
    setLabPeriods([]);
  }, [scopeId]);

  // ── Conteo de empleados (reactivo al scope) ──────────────────────────────────
  useEffect(() => {
    if (!scopeId) {
      setEmployeeCount(null);
      return;
    }
    let cancelled = false;
    const request = isBranchScope
      ? employeesApi.getEmployeeCountByBranch(scopeId)
      : employeesApi.getEmployeeCount(scopeId);

    request
      .then(count => { if (!cancelled) setEmployeeCount(count); })
      .catch(() => { if (!cancelled) setEmployeeCount(null); });

    return () => { cancelled = true; };
  }, [scopeId, isBranchScope]);

  // ── Survey counts (reactivo al scope) ────────────────────────────────────────
  useEffect(() => {
    if (!scopeId) {
      setSurveyCounts(null);
      setApplicationCounts({});
      setLoadingCounts(false);
      return;
    }
    let cancelled = false;
    setLoadingCounts(true);
    Promise.all([
      companyAnalyticsApi.getSurveyCounts(scopeId),
      companyAnalyticsApi.getApplicationCounts(scopeId),
    ])
      .then(([counts, appCounts]) => {
        if (cancelled) return;
        setSurveyCounts(counts);
        setApplicationCounts(appCounts);
      })
      .catch(err => { if (!cancelled) console.error('Error counts:', err); })
      .finally(() => { if (!cancelled) setLoadingCounts(false); });
    return () => { cancelled = true; };
  }, [scopeId]);

  // ── Carga HRA ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showHra) { setLoadingHra(false); return; }
    if (!scopeId) return;
    let cancelled = false;
    setLoadingHra(true);
    Promise.all([
      companyAnalyticsApi.getHraMetabolicRisk(scopeId),
      companyAnalyticsApi.getHraImc(scopeId),
      companyAnalyticsApi.getHraTopDiseases(scopeId),
    ])
      .then(([m, i, d]) => {
        if (!cancelled) { setHraMetabolic(m); setHraImc(i); setHraDiseases(d); }
      })
      .catch(err => { if (!cancelled) console.error('Error HRA:', err); })
      .finally(() => { if (!cancelled) setLoadingHra(false); });
    return () => { cancelled = true; };
  }, [showHra, scopeId]);

  // ── Carga períodos InBody ────────────────────────────────────────────────────
  useEffect(() => {
    if (!showInbody || !scopeId) return;
    let cancelled = false;
    const fetchFn = isBranchScope
      ? () => inbodyApi.getPeriodsForBranch(scopeId)
      : () => inbodyApi.getPeriodsForCompany(scopeId);
    fetchFn()
      .then(records => { if (!cancelled) setIbPeriods(deduplicatePeriods(records)); })
      .catch(err => { if (!cancelled) console.error('Error períodos InBody:', err); });
    return () => { cancelled = true; };
  }, [showInbody, scopeId, isBranchScope]);

  // ── Carga InBody ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showInbody) { setLoadingIb(false); return; }
    if (!scopeId) return;
    let cancelled = false;
    setLoadingIb(true);
    Promise.all([
      companyAnalyticsApi.getInbodyBmi(scopeId, selectedIbPeriod),
      companyAnalyticsApi.getInbodyPbf(scopeId, selectedIbPeriod),
      companyAnalyticsApi.getInbodyVfl(scopeId, selectedIbPeriod),
    ])
      .then(([bmi, pbf, vfl]) => {
        if (!cancelled) { setIbBmi(bmi); setIbPbf(pbf); setIbVfl(vfl); }
      })
      .catch(err => { if (!cancelled) console.error('Error InBody:', err); })
      .finally(() => { if (!cancelled) setLoadingIb(false); });
    return () => { cancelled = true; };
  }, [showInbody, scopeId, selectedIbPeriod]);

  // ── Carga períodos Lab ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    const fetchFn = isBranchScope
      ? () => labResultsApi.getPeriodsForBranch(scopeId)
      : () => labResultsApi.getPeriodsForCompany(scopeId);
    fetchFn()
      .then(records => { if (!cancelled) setLabPeriods(deduplicatePeriods(records)); })
      .catch(err => { if (!cancelled) console.error('Error períodos Lab:', err); });
    return () => { cancelled = true; };
  }, [scopeId, isBranchScope]);

  // ── Carga Lab Results ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    setLoadingLab(true);
    Promise.all([
      companyAnalyticsApi.getLabAnalytics(scopeId, selectedLabPeriod),
      companyAnalyticsApi.getTygAnalytics(scopeId, selectedLabPeriod),
    ])
      .then(([lab, tyg]) => {
        if (!cancelled) { setLabData(lab); setTygData(tyg); }
      })
      .catch(err => { if (!cancelled) console.error('Error Lab:', err); })
      .finally(() => { if (!cancelled) setLoadingLab(false); });
    return () => { cancelled = true; };
  }, [scopeId, selectedLabPeriod]);

  // ── Cruces disponibles ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    companyAnalyticsApi.getAvailableCrosses(scopeId)
      .then(crosses => { if (!cancelled) setAvailableCrosses(crosses); })
      .catch(err => { if (!cancelled) console.error('Error cruces disponibles:', err); });
    return () => { cancelled = true; };
  }, [scopeId]);

  // ── Riesgo Metabólico Real ───────────────────────────────────────────────────
  useEffect(() => {
    if (!scopeId) return;
    let cancelled = false;
    setLoadingMr(true);
    companyAnalyticsApi.getMetabolicRiskReal(scopeId, selectedCross)
      .then(data => { if (!cancelled) setMrData(data); })
      .catch(err => { if (!cancelled) console.error('Error riesgo metabólico real:', err); })
      .finally(() => { if (!cancelled) setLoadingMr(false); });
    return () => { cancelled = true; };
  }, [scopeId, selectedCross]);

  // ── Manejador del dropdown de sucursales ─────────────────────────────────────
  const handleBranchChange = (e) => {
    const value = e.target.value;
    if (!value) {
      setSelectedBranch(null);
    } else {
      const branch = branches.find(b => String(b._id) === value);
      setSelectedBranch(branch ?? null);
    }
  };

  // ── Entidades para la tabla de surveys ───────────────────────────────────────
  // General: todas las branches (o company si no hay branches)
  // Sucursal: solo la branch seleccionada
  const surveyEntities = selectedBranch
    ? [selectedBranch]
    : (branches.length > 0 ? branches : (company ? [company] : []));

  const activeSurveys = selectedBranch
    ? (selectedBranch.surveys?.filter(s => s.active).length ?? 0)
    : branches.reduce((sum, b) => sum + (b.surveys?.filter(s => s.active).length ?? 0), 0)
      || (company?.surveys?.filter(s => s.active).length ?? 0);
  const displayedScope = activeBranchScope && selectedBranch ? selectedBranch : company;
  const avatarColor   = displayedScope ? getAvatarColor(displayedScope.name) : '#00be9c';
  const initials      = displayedScope ? getInitials(displayedScope.name) : '—';

  const showLab = !loadingLab &&
    (buildBarData(labData).length > 0 || buildSecondaryBarData(labData, tygData).length > 0);
  const showMrReal = !loadingMr && mrData?.distribution?.some(d => d.value > 0);

  const configurationIncomplete = !activeBranchScope && !loadingBase && company && (
    company.configurationCompleted === false || branches.length === 0
  );

  if (configurationIncomplete) {
    return (
      <div className={styles.container}>
        {showSidebar && <AppSidebar />}
        <div className={styles.main}>
          <button className={styles.backBtn} onClick={() => navigate('/empresas')}>
            <span className={styles.backArrow}>←</span> Volver al panel
          </button>
          <div className={styles.incompleteCard}>
            <div className={styles.incompleteIcon}>!</div>
            <h1>La empresa no se ha terminado de configurar</h1>
            <p>
              Agrega al menos una sucursal y finaliza la configuración para habilitar su dashboard.
            </p>
            <button onClick={() => navigate(`/company/${company._id}`)}>Configurar empresa</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {showSidebar && <AppSidebar />}
      <div className={styles.main}>

        {/* Este control solo pertenece al flujo de superadministración. */}
        {!activeBranchScope && (
          <button className={styles.backBtn} onClick={() => navigate('/empresas')}>
            <span className={styles.backArrow}>←</span> Volver al panel
          </button>
        )}

        {/* Cabecera empresa */}
        <div className={styles.companyHeader}>
          <div className={styles.avatar} style={displayedScope?.logoUrl ? {} : { background: avatarColor }}>
            {displayedScope?.logoUrl
              ? <img src={displayedScope.logoUrl} alt={displayedScope.name} className={styles.avatarImg} />
              : initials}
          </div>
          <div className={styles.companyInfo}>
            {loadingBase
              ? <span className={styles.skeletonName} />
              : <h1 className={styles.companyName}>{displayedScope?.name}</h1>}
            <p className={styles.companySub}>
              {!loadingBase && `${employeeCount ?? '—'} empleados · ${activeSurveys} cuestionarios activos`}
            </p>
          </div>
          {/* Dropdown de sucursales */}
          {!activeBranchScope && !loadingBase && branches.length > 0 && (
            <select
              className={styles.branchFilter}
              value={selectedBranch ? String(selectedBranch._id) : ''}
              onChange={handleBranchChange}
            >
              <option value="">General</option>
              {branches.map(b => (
                <option key={String(b._id)} value={String(b._id)}>{b.name}</option>
              ))}
            </select>
          )}
        </div>

        {/* Stats */}
        <div className={styles.statsGrid}>
          <StatCard icon={<PeopleIcon />} value={employeeCount ?? '—'} label="Empleados"
            loading={loadingBase} iconColor="var(--color-delilife, #00be9c)" />
          <StatCard icon={<SurveyIcon />} value={activeSurveys} label="Cuestionarios Activos"
            loading={loadingBase} iconColor="#6f42c1" />
        </div>

        {/* ── Sección HRA ──────────────────────────────────────────────────── */}
        {!loadingBase && showHra && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Health Risk Assessment</h2>
              <SexTabs active={sexTabHra} onChange={setSexTabHra} />
            </div>
            <div className={styles.chartsGrid}>
              <DonutChart title="Riesgo Metabólico"
                data={hraMetabolic?.[sexTabHra]} colors={METABOLIC_COLORS} loading={loadingHra}
                details={HRA_METABOLIC_DETAILS} />
              <DonutChart title="IMC (Índice de Masa Corporal)"
                data={hraImc?.[sexTabHra]} colors={IMC_COLORS} loading={loadingHra}
                startAngle={90} details={HRA_IMC_DETAILS} />
              <TopDiseases data={hraDiseases?.[sexTabHra]} loading={loadingHra} />
            </div>
          </section>
        )}

        {/* ── Sección InBody ───────────────────────────────────────────────── */}
        {!loadingBase && showInbody && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>InBody</h2>
              <div className={styles.sectionControls}>
                <SexTabs active={sexTabIb} onChange={setSexTabIb} />
                <PeriodDropdown periods={ibPeriods} value={selectedIbPeriod} onChange={setSelectedIbPeriod} />
              </div>
            </div>
            <div className={styles.chartsGrid}>
              <DonutChart title="IMC (Índice de Masa Corporal) — InBody"
                data={ibBmi?.[sexTabIb]} colors={IMC_COLORS} loading={loadingIb}
                details={INBODY_IMC_DETAILS} />
              <DonutChart title="PBF (Porcentaje de Grasa Corporal)"
                data={ibPbf?.[sexTabIb]} colors={INBODY_PBF_COLORS} loading={loadingIb}
                details={INBODY_PBF_DETAILS} />
              <DonutChart title="VFL (Nivel de Grasa Visceral)"
                data={ibVfl?.[sexTabIb]} colors={INBODY_VFL_COLORS} loading={loadingIb}
                details={INBODY_VFL_DETAILS} />
            </div>
          </section>
        )}

        {/* ── Resultados de Laboratorio ────────────────────────────────────── */}
        {showLab && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Resultados de Laboratorio</h2>
              <PeriodDropdown periods={labPeriods} value={selectedLabPeriod} onChange={setSelectedLabPeriod} />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <StackedLabChart labData={labData} loading={loadingLab} />
              <StackedSecondaryChart labData={labData} tygData={tygData} loading={loadingLab} />
            </div>
          </section>
        )}

        {/* ── Riesgo Metabólico Real ──────────────────────────────────────── */}
        {(showMrReal || availableCrosses.length > 0) && (
          <section className={styles.section}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Riesgo Metabólico Real</h2>
              {availableCrosses.length > 0 && (
                <select
                  className={styles.periodSelect}
                  value={selectedCross ? `${selectedCross.ibPeriod.month}-${selectedCross.ibPeriod.year}-${selectedCross.labPeriod.month}-${selectedCross.labPeriod.year}` : ''}
                  onChange={e => {
                    if (!e.target.value) { setSelectedCross(null); return; }
                    const [ibM, ibY, labM, labY] = e.target.value.split('-').map(Number);
                    setSelectedCross({ ibPeriod: { month: ibM, year: ibY }, labPeriod: { month: labM, year: labY } });
                  }}
                >
                  <option value="">Todos los cruces</option>
                  {availableCrosses.map(c => {
                    const k = `${c.ibPeriod.month}-${c.ibPeriod.year}-${c.labPeriod.month}-${c.labPeriod.year}`;
                    return (
                      <option key={k} value={k}>
                        {`InBody ${MONTH_NAMES[c.ibPeriod.month - 1]} ${c.ibPeriod.year} × QS (Química Sanguínea) ${MONTH_NAMES[c.labPeriod.month - 1]} ${c.labPeriod.year}`}
                      </option>
                    );
                  })}
                </select>
              )}
            </div>
            <div style={{ marginTop: '1rem' }}>
              <MetabolicRiskDonutChart
                data={mrData?.distribution}
                loading={loadingMr}
                ibPeriods={mrData?.ibPeriods}
                labPeriods={mrData?.labPeriods}
                details={METABOLIC_RISK_DETAILS}
              />
            </div>
          </section>
        )}

        {!loadingBase && (
          <Nom035ApplicationsSection entities={surveyEntities} company={company}
            showCompanyTotals={canViewCompanyNomTotals}
            counts={surveyCounts} applicationCounts={applicationCounts}
            loading={loadingCounts} navigate={navigate} />
        )}

        {/* ── Cuestionarios de Bienestar ───────────────────────────────────── */}
        {!loadingBase && (
          <section className={styles.section}>
            <SurveysTable
              entities={surveyEntities}
              counts={surveyCounts}
              applicationCounts={applicationCounts}
              loading={loadingCounts}
              navigate={navigate}
            />
          </section>
        )}

      </div>
    </div>
  );
}
